import { usePersistedState } from "@/lib/persistedFilters";
// Iter 311 — partner self-serve: the deals behind one period, each expandable
// to its products and tracked items (DealLinesPanel, same scoped endpoint).
import { Fragment, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Download, Loader2, Package, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DealLinesPanel } from "./DealLinesPanel";
import { NewPill } from "./NewPill";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const decodeHtml = (s) => (s || "").replace(/&#(\d+);/g, (_, n) => String.fromCharCode(parseInt(n, 10)))
  .replace(/&amp;/g, "&").replace(/&quot;/g, "\"").replace(/&#039;|&apos;/g, "'");

export function MyDealsCard({ periodLabel }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = usePersistedState("my-deals.q", "");
  const [expanded, setExpanded] = useState(null);
  const [downloading, setDownloading] = useState("");

  const downloadCsv = async (detail) => {
    setDownloading(detail);
    try {
      const res = await api.get("/commissions/my-deals.csv", { params: { period_label: periodLabel, detail }, responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || `deals-${periodLabel}-${detail}.csv`;
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${name}`);
    } catch (e) {
      let detailMsg = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) { try { detailMsg = JSON.parse(await e.response.data.text())?.detail; } catch { /* not JSON */ } }
      toast.error(formatApiErrorDetail(detailMsg) || "Download failed");
    } finally {
      setDownloading("");
    }
  };

  useEffect(() => {
    if (!periodLabel) return undefined;
    let alive = true;
    setData(null); setErr(""); setExpanded(null);
    api.get("/commissions/my-deals", { params: { period_label: periodLabel } })
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not load your deals"));
    return () => { alive = false; };
  }, [periodLabel]);

  const items = useMemo(() => {
    const all = data?.items || [];
    const t = q.trim().toLowerCase();
    return t ? all.filter((d) => `${d.domain} ${d.deal_name} ${d.agent}`.toLowerCase().includes(t)) : all;
  }, [data, q]);
  const manyAgents = useMemo(() => new Set((data?.items || []).map((d) => d.agent)).size > 1, [data]);

  if (!periodLabel) return null;
  return (
    <Card className="border-slate-200 shadow-sm overflow-hidden mt-4" data-testid="my-deals-card">
      <div className="flex flex-wrap items-center gap-3 px-3 py-2 border-b border-slate-100 bg-slate-50/50">
        <div className="text-[10px] uppercase tracking-wider font-medium text-slate-500 inline-flex items-center gap-1.5">
          <Package className="h-3.5 w-3.5" /> Your deals — {periodLabel}{data ? ` (${data.totals.deals})` : ""}
        </div>
        {data && (
          <div className="text-xs text-slate-500 tabular-nums">
            Revenue <b className="text-slate-700">{money(data.totals.revenue)}</b> · Commission <b className="text-emerald-700">{money(data.totals.commission)}</b>
            {data.totals.payable_commission !== data.totals.commission && <> · Payable <b className="text-emerald-700">{money(data.totals.payable_commission)}</b></>}
            {data.totals.new_deals > 0 && (
              <span className="ml-2 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-200"
                    title="Customers on a commission statement for the first time" data-testid="my-deals-new-count">
                {data.totals.new_deals} new deal{data.totals.new_deals === 1 ? "" : "s"}
              </span>
            )}
          </div>
        )}
        <div className="relative ml-auto w-56">
          <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search domain or customer…"
                 className="pl-7 h-8 text-xs" data-testid="my-deals-search" />
        </div>
        <div className="inline-flex rounded-md border border-slate-200 bg-white text-xs shadow-sm" data-testid="my-deals-csv-group">
          <button type="button" onClick={() => downloadCsv("items")} disabled={downloading !== "" || !data?.items?.length}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-slate-700 hover:bg-slate-50 disabled:opacity-50 rounded-l-md"
                  title="One row per tracked item, with its deal, product, billed amount, rate, commission and invoice"
                  data-testid="my-deals-csv-items">
            {downloading === "items" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            CSV · tracked items
          </button>
          <button type="button" onClick={() => downloadCsv("products")} disabled={downloading !== "" || !data?.items?.length}
                  className="inline-flex items-center gap-1 border-l border-slate-200 px-2.5 py-1.5 text-slate-700 hover:bg-slate-50 disabled:opacity-50 rounded-r-md"
                  title="One row per deal and product, with quantity, items, billed amount, rate and commission"
                  data-testid="my-deals-csv-products">
            {downloading === "products" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            CSV · by product
          </button>
        </div>
      </div>
      <div className="px-3 py-1.5 text-[11px] text-slate-500 border-b border-slate-100">
        Click a domain to see what was billed under it — grouped per product and every tracked item (extensions, numbers, channels…).
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="my-deals-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Domain</th>
              <th className="px-3 py-2 text-left">Customer</th>
              {manyAgents && <th className="px-3 py-2 text-left">Agent</th>}
              <th className="px-3 py-2 text-left">Level</th>
              <th className="px-3 py-2 text-right">Revenue</th>
              <th className="px-3 py-2 text-right">Commission</th>
              <th className="px-3 py-2 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {err ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-sm text-rose-600" data-testid="my-deals-error">{err}</td></tr>
            ) : !data ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-slate-400" data-testid="my-deals-empty">
                {q ? `No deals match “${q}”.` : "No deal-level detail for this period yet."}
              </td></tr>
            ) : items.map((d) => {
              const open = expanded === d.deal_id;
              const colSpan = manyAgents ? 7 : 6;
              return (
                <Fragment key={d.deal_id}>
                  <tr className="hover:bg-slate-50/50" data-testid={`my-deal-row-${d.deal_id}`}>
                    <td className="px-3 py-2 text-xs text-slate-700">
                      <button type="button" onClick={() => setExpanded(open ? null : d.deal_id)}
                              className="inline-flex items-center gap-1 text-left hover:text-slate-900"
                              data-testid={`my-deal-expand-${d.deal_id}`}>
                        {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />}
                        <span className="underline-offset-2 hover:underline">{decodeHtml(d.domain) || "—"}</span>
                        {d.is_new && <NewPill kind="deal" testId={`my-deal-new-${d.deal_id}`} />}
                        {d.line_count ? <span className="text-[10px] text-slate-400">· {d.line_count} item{d.line_count === 1 ? "" : "s"}</span> : null}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{decodeHtml(d.deal_name)}</td>
                    {manyAgents && <td className="px-3 py-2 text-xs text-slate-500">{d.agent}</td>}
                    <td className="px-3 py-2 text-xs">{d.level_name}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(d.total)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium text-emerald-700">{money(d.commission)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ${d.payable ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                            title={d.payable ? "Counts toward your payout" : d.invoice_paid ? "Held" : "Waiting for the customer's invoice to be paid"}>
                        {d.payable ? "Payable" : "Held"}
                      </span>
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-slate-50/70" data-testid={`my-deal-detail-${d.deal_id}`}>
                      <td colSpan={colSpan} className="p-0 border-l-2 border-emerald-300">
                        <DealLinesPanel dealId={d.deal_id} showReportLink={false} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
